import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { cases } from "@/lib/endpoints";
import { isMockEnabled } from "@/lib/mock";
import type { CheckoutResponse } from "@/lib/api-types";
import { FieldHelpButton } from "@/components/FieldHelpButton";
import { cn } from "@/lib/utils";

/**
 * StepCheckout — pay the N-400 filing fee before generating the packet.
 *
 * Two modes:
 *  • Production: POST /checkout → redirect to Stripe Hosted Checkout.
 *  • Mock / dev: "Simulate payment" button → GET /dev-mark-paid.
 *
 * Skips itself automatically when paid_at is already set.
 */
export function StepCheckout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const caseQ = useQuery({
    queryKey: ["case", id],
    queryFn: () => cases.get(id!),
    enabled: !!id,
    staleTime: 0,
  });

  const caseData = caseQ.data;
  const isPaid = !!caseData?.paid_at;
  const feeDollars = caseData?.fee_total_cents != null
    ? (caseData.fee_total_cents / 100).toFixed(2)
    : "710.00";

  const checkoutMut = useMutation<CheckoutResponse, Error, void>({
    mutationFn: () => cases.checkout(id!),
    onSuccess: (resp) => {
      // When the backend has DEV_AUTO_PAY enabled the same /checkout
      // endpoint returns an empty checkout_url after marking the case
      // paid in-place. Skip the Stripe redirect and refetch the case
      // so the "Payment confirmed" branch renders.
      if (resp.checkout_url) {
        window.location.href = resp.checkout_url;
        return;
      }
      qc.invalidateQueries({ queryKey: ["case", id] });
    },
  });

  const devPayMut = useMutation<unknown, Error, void>({
    mutationFn: () => cases.devMarkPaid(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case", id] }),
  });

  const isLoading = caseQ.isLoading;
  const isBusy = checkoutMut.isPending || devPayMut.isPending;

  // ── Already paid ──────────────────────────────────────────────────────────
  if (!isLoading && isPaid) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900">Filing fee</h2>
          <p className="text-slate-500 mt-1 text-sm">Your payment has been received.</p>
        </div>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center shadow-card">
          <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-emerald-800 font-bold font-display text-xl">
            Payment confirmed — ${feeDollars}
          </div>
          <p className="text-emerald-600 text-sm mt-1">
            Your N-400 packet is ready to generate.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate(`/cases/${id}/wizard/review`)}
          className="w-full rounded-xl bg-slate-900 text-white px-4 py-3 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
        >
          Continue to review →
        </button>
      </div>
    );
  }

  // ── Unpaid ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold font-display text-slate-900">Filing fee</h2>
        <p className="text-slate-500 mt-1 text-sm leading-relaxed">
          Pay the official USCIS N-400 fee to unlock your completed packet.
          You'll be redirected to a secure Stripe checkout page.
        </p>
      </div>

      {/* Fee card */}
      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-card overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-semibold text-slate-900 text-base">N-400 Application Fee</div>
              <div className="text-sm text-slate-400 mt-0.5">One-time · Non-refundable once submitted</div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold font-display tabular-nums text-slate-900 inline-flex items-center gap-1">
                ${feeDollars}
                <FieldHelpButton question="Who receives the $710 fee? Is this separate from GetCitizen's fee?" />
              </div>
              <div className="text-xs text-slate-400 mt-0.5">USCIS fee · 2025</div>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-3 gap-4">
            {[
              { icon: "📄", label: "Completed N-400 PDF" },
              { icon: "✓",  label: "USCIS-ready format" },
              { icon: "🔒", label: "Secure Stripe payment" },
            ].map(({ icon, label }) => (
              <div key={label} className="flex flex-col items-center text-center gap-1.5">
                <span className="text-lg">{icon}</span>
                <span className="text-[11px] text-slate-500 font-medium leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Social proof bar */}
        <div className="bg-slate-50 border-t border-slate-100 px-6 py-3 flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {["bg-blue-400","bg-teal-400","bg-indigo-400","bg-emerald-400"].map((c, i) => (
              <div key={i} className={cn("w-6 h-6 rounded-full border-2 border-white", c)} />
            ))}
          </div>
          <p className="text-xs text-slate-500">
            <span className="font-semibold text-slate-700">2,412 applicants</span> have paid and generated their packet this year
          </p>
        </div>
      </div>

      {/* Testimonials */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {TESTIMONIALS.map((t) => (
          <div key={t.name} className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm">
            <div className="flex gap-0.5 mb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} className="w-3 h-3 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">"{t.quote}"</p>
            <div className="mt-2.5 flex items-center gap-1.5">
              <div className={cn("w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] font-bold", t.color)}>
                {t.name[0]}
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-700">{t.name}</div>
                <div className="text-[10px] text-slate-400">{t.city}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {(checkoutMut.isError || devPayMut.isError) && (
        <p className="text-sm text-red-500">
          {(checkoutMut.error ?? devPayMut.error)?.message}
        </p>
      )}

      {/* Mock mode */}
      {isMockEnabled ? (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => devPayMut.mutate()}
            disabled={isBusy || isLoading}
            className="w-full rounded-xl bg-amber-500 text-white px-4 py-3 text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            {devPayMut.isPending ? "Processing…" : "⚡ Simulate payment (mock mode)"}
          </button>
          <p className="text-xs text-center text-amber-600">
            MOCK — no real payment. In production this redirects to Stripe.
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => checkoutMut.mutate()}
          disabled={isBusy || isLoading}
          className="w-full rounded-xl bg-slate-900 text-white px-4 py-3 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
        >
          {checkoutMut.isPending ? "Redirecting to Stripe…" : `Pay $${feeDollars} →`}
        </button>
      )}

      {/* Stripe trust line */}
      <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Secured by Stripe · We never store your card details · 256-bit TLS
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

const TESTIMONIALS = [
  {
    quote: "Got my N-400 filled out in one evening. The form was perfect — I submitted it the next morning.",
    name: "Maria S.",
    city: "Los Angeles, CA",
    color: "bg-blue-500",
  },
  {
    quote: "I was nervous about making mistakes. This walked me through everything and the PDF was exactly right.",
    name: "David K.",
    city: "New York, NY",
    color: "bg-teal-500",
  },
  {
    quote: "Worth every penny. Saved me from hiring a $3,000 attorney for a straightforward case.",
    name: "Priya M.",
    city: "Austin, TX",
    color: "bg-indigo-500",
  },
];
